import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HandHeart, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { API } from '../../App';

/**
 * Support-level picker. Shown right after a user joins a circle so they can
 * declare *how* they can show up — capacity/proximity, NOT commitment.
 *
 * Anti-ranking design:
 *  - Same size, same accent color for both options
 *  - Concentric ring iconography (Ring Theory native), not stars/steps
 *  - Explicit "change any time" copy
 *  - Skippable → server defaults to `active`
 */
const SupportLevelDialog = ({ open, onOpenChange, circleId, circleName, onDone }) => {
  const [saving, setSaving] = useState(null);

  const pick = async (level) => {
    setSaving(level);
    try {
      await axios.put(`${API}/circles/${circleId}/my-support-level`, { support_level: level });
      toast.success(level === 'active' ? "You're in as Active Support 🤝" : "You're in as Community Support 💌");
      onOpenChange(false);
      if (onDone) onDone(level);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save — please try again');
    }
    setSaving(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface-card max-w-md" data-testid="support-level-dialog">
        <DialogHeader>
          <DialogTitle>How can you show up right now?</DialogTitle>
          <DialogDescription>
            There are lots of ways to support {circleName || 'this circle'}. Pick what feels right for you today — you can change it any time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <button
            onClick={() => pick('active')}
            disabled={!!saving}
            className="w-full text-left p-4 rounded-2xl border border-border/60 hover:border-cyan-400 hover:bg-cyan-50/50 dark:hover:bg-cyan-900/10 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="support-level-active"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center flex-shrink-0">
                <HandHeart className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">Active Support</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  I can actively help — rides, meals, childcare, errands, home support, appointments.
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => pick('community')}
            disabled={!!saving}
            className="w-full text-left p-4 rounded-2xl border border-border/60 hover:border-cyan-400 hover:bg-cyan-50/50 dark:hover:bg-cyan-900/10 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="support-level-community"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">Community Support</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  I can support from farther out — sending a meal delivery, occasional check-ins, gifts, financial support.
                </p>
              </div>
            </div>
          </button>

          <p className="text-[11px] text-muted-foreground text-center pt-1">
            You can change this any time. Capacity shifts, and that{"'"}s okay.
          </p>

          <Button
            variant="ghost"
            className="w-full text-xs text-muted-foreground"
            onClick={() => onOpenChange(false)}
            disabled={!!saving}
            data-testid="support-level-skip"
          >
            Skip for now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupportLevelDialog;
